import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { supabase } from './supabase';
import { errorReporter } from '../utils/errorReporting';
import { logger } from '../utils/logger';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  initialized: boolean;
  user: { id: string; email: string } | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
}

const SESSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes

// Track login attempts
const loginAttempts = new Map<string, { count: number; lastAttempt: number }>();

// Helper function to clear all auth data
const clearAuthData = async () => {
  try {
    await supabase.auth.signOut();
  } catch (error) {
    logger.error('Error during signOut:', error);
  }
  
  // Clear all auth-related storage
  try {
    localStorage.removeItem('sb-auth-token');
    localStorage.removeItem('supabase.auth.token');
    localStorage.removeItem('supabase.auth.refreshToken');
    sessionStorage.clear();
  } catch (error) {
    logger.error('Error clearing storage:', error);
  }
};

export const useAuth = create<AuthState>()(
  persist(
    (set) => ({
      isAuthenticated: false,
      isLoading: false,
      error: null,
      initialized: false,
      user: null,

      checkSession: async () => {
        try {
          set({ isLoading: true, error: null });
          
          // Add timeout to prevent hanging requests
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 8000);
          
          // Get current session
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          clearTimeout(timeoutId);
          
          if (sessionError) {
            // Handle specific refresh token errors
            if (sessionError.message?.toLowerCase().includes('refresh_token') || 
                sessionError.message?.toLowerCase().includes('invalid token')) {
              await clearAuthData();
              set({ 
                isAuthenticated: false,
                isLoading: false,
                initialized: true,
                error: null,
                user: null
              });
              return;
            }
            throw sessionError;
          }
          
          // If no session exists, clear state and return
          if (!session) {
            await clearAuthData();
            set({ 
              isAuthenticated: false,
              isLoading: false,
              initialized: true,
              error: null,
              user: null
            });
            return;
          }

          // Verify admin access
          const { data: adminUser, error: adminError } = await supabase
            .from('admin_users')
            .select('id, email')
            .eq('id', session.user.id)
            .maybeSingle();

          if (adminError || !adminUser) {
            // Clear invalid session
            await clearAuthData();
            
            set({ 
              isAuthenticated: false,
              isLoading: false,
              initialized: true,
              error: adminError ? 'Error verifying admin access' : 'User not found in admin users',
              user: null
            });
            return;
          }

          // Valid session and admin user found
          set({ 
            isAuthenticated: true,
            isLoading: false,
            initialized: true,
            error: null,
            user: {
              id: adminUser.id,
              email: adminUser.email
            }
          });
        } catch (error) {
          // Clear all auth data on error
          await clearAuthData();
          
          set({ 
            isAuthenticated: false,
            isLoading: false,
            initialized: true,
            error: 'Failed to check authentication status',
            user: null
          });
        }
      },

      login: async (email: string, password: string) => {
        const attempts = loginAttempts.get(email) || { count: 0, lastAttempt: 0 };
        const now = Date.now();

        // Check if user is locked out
        if (attempts.count >= MAX_LOGIN_ATTEMPTS && 
            now - attempts.lastAttempt < LOCKOUT_DURATION) {
          const remainingTime = Math.ceil((LOCKOUT_DURATION - (now - attempts.lastAttempt)) / 60000);
          set({ 
            error: `Too many login attempts. Please try again in ${remainingTime} minutes.`,
            isLoading: false 
          });
          return false;
        }

        // Reset attempts if lockout period has passed
        if (now - attempts.lastAttempt > LOCKOUT_DURATION) {
          attempts.count = 0;
        }

        set({ isLoading: true, error: null });

        try {
          // Clear any existing sessions before login
          await clearAuthData();
          
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Login timeout')), 15000);
          });
          
          const { data: signInData, error: signInError } = await Promise.race([
            supabase.auth.signInWithPassword({
            email,
            password
          }),
            timeoutPromise
          ]) as any;

          if (signInError) {
            attempts.count++;
            attempts.lastAttempt = now;
            loginAttempts.set(email, attempts);

            const remainingAttempts = MAX_LOGIN_ATTEMPTS - attempts.count;
            set({ 
              error: `Invalid email or password. ${remainingAttempts} attempts remaining.`,
              isLoading: false 
            });
            return false;
          }

          if (!signInData.user) {
            set({ error: 'Authentication failed', isLoading: false });
            return false;
          }

          const { data: adminUser, error: adminError } = await supabase
            .from('admin_users')
            .select('id, email')
            .eq('id', signInData.user.id)
            .maybeSingle();

          if (adminError || !adminUser) {
            await clearAuthData();
            set({ 
              error: adminError ? 'Error verifying admin access' : 'User not found in admin users',
              isLoading: false 
            });
            return false;
          }

          // Clear login attempts on successful login
          loginAttempts.delete(email);

          set({ 
            isAuthenticated: true, 
            isLoading: false, 
            error: null,
            user: {
              id: signInData.user.id,
              email: signInData.user.email || ''
            }
          });
          return true;
        } catch (error) {
          await clearAuthData();
          set({ error: 'An error occurred during login', isLoading: false });
          return false;
        }
      },

      logout: async () => {
        try {
          await clearAuthData();
          set({ 
            isAuthenticated: false, 
            error: null,
            initialized: true,
            user: null
          });
        } catch (error) {
          // Force clear state even if logout fails
          await clearAuthData();
          set({ 
            isAuthenticated: false, 
            error: null,
            initialized: true,
            user: null
          });
        }
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({ 
        isAuthenticated: state.isAuthenticated,
        initialized: state.initialized,
        user: state.user
      }),
    }
  )
);

// Initialize auth state
if (!useAuth.getState().initialized) {
  useAuth.getState().checkSession();
}

// Less frequent session checks
setInterval(() => {
  if (useAuth.getState().isAuthenticated) {
    useAuth.getState().checkSession();
  }
}, SESSION_CHECK_INTERVAL);

// Listen for storage events to sync auth state across tabs
window.addEventListener('storage', (event) => {
  if (event.key === 'auth-storage' && event.newValue === null) {
    useAuth.getState().logout();
  }
});